/**
 * Servis API — typed wrappers around `/v1/servis/*`.
 *
 * Mirrors the OpenAPI shapes (`WeekViewResponseSchema`, etc.) in
 * `api/src/schemas/servis.ts`. Re-declared here so the module compiles when
 * the gitignored `frontend/src/types/api.ts` hasn't been refreshed.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { components } from '@/types/api';
import type { HistorieZaznam } from '@/modules/historie/api';

export interface ServisMechanic {
  id_opravce: number;
  jmeno: string;
  id_firmy: number;
}

/** Kind of an internal event (typ_udalosti 2); null = Jiné (free-form úkon). */
export type ServisDruhUkonu = 'servis' | 'stk' | 'servis_stk';

/** Confirmation that an event took place; null/absent = not confirmed. */
export type ServisPotvrzeniStav = 'probehlo' | 'neprobehlo';

export interface ServisEvent {
  id_udalosti: number;
  typ_udalosti: number;
  id_opravce: number;
  jmeno_zakaznika: string;
  kontakt: string;
  email: string;
  vozidlo: string;
  spz_vozidla: string;
  /** VIN of the car (migration 0042); '' = not set. */
  vin?: string;
  servisni_ukon: string;
  druh_ukonu?: ServisDruhUkonu | null;
  datum_od: string;
  datum_do: string;
  nahradni_vozidlo: string;
  id_firmy: number;
  id_uzivatele: number;
  id_zakaznika: number;
  reminder_odeslan_at: string | null;
  /** 1 = the customer gets no e-mails for this order (migration 0093); absent on older payloads = 0. */
  email_vypnut?: number;
  smazano_at: string | null;
  smazal_id_uzivatele: number | null;
  datum_vytvoreni: string | null;
  barva: string | null;
  /** Confirmation state (migration 0041). Server-computed flags follow the DB clock. */
  potvrzeni_stav?: ServisPotvrzeniStav | null;
  potvrzeno_at?: string | null;
  potvrdil_id_uzivatele?: number | null;
  /** Confirmable type, not deleted, already started. */
  lze_potvrdit?: boolean;
  /** `lze_potvrdit`, unconfirmed, started within the last 72 h — flagged in the UI. */
  k_potvrzeni?: boolean;
}

export interface ServisDay {
  date: string;
  dayName: string;
  events: ServisEvent[];
}

export interface ServisWeekInfo {
  number: number;
  year: number;
  offset: number;
}

export interface ServisWeekResponse {
  week: ServisWeekInfo;
  mechanics: ServisMechanic[];
  days: ServisDay[];
  readOnly: boolean;
  serverNow: string;
}

export function useServisWeekQuery(offset: number) {
  return useQuery({
    queryKey: ['servis', 'week', offset],
    queryFn: ({ signal }) =>
      api.get<ServisWeekResponse>(
        `/v1/servis/events?offset=${offset}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

/** One car from `/v1/servis/vehicle-search` (nové + ojeté; ojeté wins when both hold the VIN). */
export interface ServisVehicleHit {
  kind: 'nove' | 'ojete';
  id_vozidla: number;
  spz: string;
  cislo_objednavky: string;
  vin: string;
  barva: string;
  stav_vozidla: string;
  znacka_nazev: string | null;
  model_nazev: string | null;
  /** Only on cars of another firma of the group (read-only projection). */
  id_firmy_vlastnika?: number | null;
  vlastnik_nazev?: string | null;
  vlastnik_nejasny?: boolean;
}

interface ServisVehicleSearchResponse {
  vehicles: ServisVehicleHit[];
}

/** SPZ-or-VIN typeahead over the company's cars. */
export function useServisVehicleSearchQuery(q: string, enabled = true) {
  const text = q.trim();
  return useQuery({
    queryKey: ['servis', 'vehicle-search', text],
    enabled: enabled && text.length >= 2,
    queryFn: ({ signal }) =>
      api.get<ServisVehicleSearchResponse>(
        `/v1/servis/vehicle-search?q=${encodeURIComponent(text)}&limit=10`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

/** One event of a car's servis history (`/v1/servis/vehicle-history`). */
export interface ServisVehicleHistoryEvent {
  id_udalosti: number;
  /** 0 = customer úkon, 2 = internal úkon. */
  typ_udalosti: number;
  datum_od: string;
  datum_do: string;
  druh_ukonu: ServisDruhUkonu | null;
  servisni_ukon: string;
  jmeno_zakaznika: string;
  jmeno_opravce: string | null;
  spz_vozidla: string;
  vin: string;
  potvrzeni_stav: ServisPotvrzeniStav | null;
}

interface ServisVehicleHistoryResponse {
  events: ServisVehicleHistoryEvent[];
  total: number;
}

/** Servis events of one car — by VIN, by plate only for events without a VIN. */
export function useServisVehicleHistoryQuery(car: { vin: string; spz: string }, enabled = true) {
  const vin = car.vin.trim();
  const spz = car.spz.trim();
  return useQuery({
    queryKey: ['servis', 'vehicle-history', vin, spz],
    enabled: enabled && (vin !== '' || spz !== ''),
    queryFn: ({ signal }) =>
      api.get<ServisVehicleHistoryResponse>(
        `/v1/servis/vehicle-history?vin=${encodeURIComponent(vin)}&spz=${encodeURIComponent(spz)}`,
        { signal },
      ),
  });
}

/** A service order's replacement car: a technical reservation of a fleet car (plate, model, term — nothing else). */
export type ServisNahradniRezervace = NonNullable<components['schemas']['ServisNahradniRezervace']>;
export type ServisNahradniStredisko = ServisNahradniRezervace['stredisko'];
/** A fleet car free for a term (plate and model only). */
export type ServisVolneVozidlo = components['schemas']['ServisNahradniVolna']['vozidla'][number];

export interface ServisEventDetail extends ServisEvent {
  /** The replacement car (null = none). Absent on list/search rows. */
  nahradni_rezervace?: ServisNahradniRezervace | null;
  creator_jmeno: string | null;
  creator_prijmeni: string | null;
  smazal_jmeno: string | null;
  smazal_prijmeni: string | null;
  potvrdil_jmeno?: string | null;
  potvrdil_prijmeni?: string | null;
}

interface ServisEventDetailResponse {
  event: ServisEventDetail;
}

export function useServisEventQuery(id: number | null) {
  return useQuery({
    queryKey: ['servis', 'event', id],
    enabled: id != null && Number.isFinite(id),
    // Seeds the servis event edit modal — always refetch on mount for fresh state.
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<ServisEventDetailResponse>(`/v1/servis/events/${id}`, { signal }),
  });
}

// ==================== EVENTS OF A RANGE (rental calendar) ====================

/** An event that touches a date range and names a car (VIN or plate) — what the rental calendar lays over the fleet. */
export type ServisKalendarUdalost = components['schemas']['ServisKalendarUdalost'];

export function useServisKalendarQuery(od: string, doDay: string, enabled = true) {
  return useQuery({
    queryKey: ['servis', 'kalendar', od, doDay],
    enabled,
    queryFn: ({ signal }) =>
      api.get<{ events: ServisKalendarUdalost[] }>(`/v1/servis/events/range?od=${od}&do=${doDay}`, { signal }),
    select: (data) => data.events,
  });
}

// ==================== CHANGE HISTORY ====================

interface ServisEventHistorieResponse {
  historie: HistorieZaznam[];
}

/** The event's change history (newest first) — the modal's Historie změn section. */
export function useServisEventHistorieQuery(id: number | null) {
  return useQuery({
    queryKey: ['servis', 'event', id, 'historie'],
    enabled: id != null && Number.isFinite(id),
    refetchOnMount: 'always',
    queryFn: ({ signal }) =>
      api.get<ServisEventHistorieResponse>(`/v1/servis/events/${id}/historie`, { signal }),
  });
}

// ==================== SEARCH (Phase 5.2) ====================

export interface ServisSearchResult extends ServisEvent {
  jmeno_opravce: string | null;
}

export interface ServisSearchFilters {
  jmeno_zakaznika?: string;
  vozidlo?: string;
  spz_vozidla?: string;
  servisni_ukon?: string;
  /** Include soft-deleted events in the results. */
  smazane?: boolean;
  /** `k_potvrzeni` = the flagged worklist; otherwise a stored confirmation state. */
  potvrzeni?: 'k_potvrzeni' | ServisPotvrzeniStav;
}

interface ServisSearchResponse {
  events: ServisSearchResult[];
}

function buildSearchQuery(filters: ServisSearchFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (typeof value === 'boolean') {
      if (value) params.set(key, '1');
    } else if (value && value.trim()) {
      params.set(key, value.trim());
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export function useServisSearchQuery(filters: ServisSearchFilters) {
  // The smazane toggle alone doesn't trigger a search — a text filter (or the
  // confirmation filter, a worklist in its own right) must be set.
  const enabled = Object.values(filters).some((v) => typeof v === 'string' && v.trim());
  return useQuery({
    queryKey: ['servis', 'search', filters],
    enabled,
    queryFn: ({ signal }) =>
      api.get<ServisSearchResponse>(
        `/v1/servis/events/search${buildSearchQuery(filters)}`,
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

// ==================== DAY PREVIEW (overlap indicator) ====================

export interface ServisPreviewResponse {
  events: ServisEvent[];
  mechanic: ServisMechanic | null;
  dayStart: string;
  dayEnd: string;
}

/**
 * Fetch a single mechanic's events for the day containing `datumOd`. Powers the
 * in-modal overlap timeline. `datumOd` may be a date ("YYYY-MM-DD") or a full
 * datetime — the server only uses the day portion. Disabled until both inputs
 * are present.
 */
export function useServisDayPreviewQuery(
  mechanicId: number | null,
  datumOd: string | null,
) {
  return useQuery({
    queryKey: ['servis', 'preview', mechanicId, datumOd],
    enabled: mechanicId != null && Number.isFinite(mechanicId) && !!datumOd,
    queryFn: ({ signal }) =>
      api.post<ServisPreviewResponse>(
        '/v1/servis/events/preview',
        { id_opravce: mechanicId, datum_od: datumOd },
        { signal },
      ),
    placeholderData: (previous) => previous,
  });
}

// ==================== MECHANICS (Phase 5.7) ====================

interface MechanicsResponse {
  mechanics: ServisMechanic[];
}

export function useMechanicsQuery() {
  return useQuery({
    queryKey: ['servis', 'mechanics'],
    queryFn: ({ signal }) =>
      api.get<MechanicsResponse>('/v1/servis/mechanics', { signal }),
    staleTime: 5 * 60 * 1000,
  });
}

// ==================== MUTATIONS (Phase 5.7) ====================
//
// Typ_udalosti discriminator → endpoint:
//   1 = Servisní úkon  → POST /events           PUT /events/{id}
//   2 = Interní úkon   → POST /internal-events  PUT /internal-events/{id}
//   3 = Dovolená       → POST /vacations        PUT /vacations/{id}
//   4 = Směna          → POST /shifts           (no update; recreate)
// DELETE /events/{id} is the universal removal endpoint (all 4 typy land in
// `servis_udalost`).

export interface ServisEventBody {
  id_opravce: number;
  datum_od: string;
  datum_do: string;
  jmeno_zakaznika?: string;
  kontakt?: string;
  email?: string;
  vozidlo?: string;
  spz_vozidla?: string;
  vin?: string;
  servisni_ukon?: string;
  /** Optional kind; `'jine'` clears it. Omitted on update = unchanged. */
  druh_ukonu?: ServisDruhUkonu | 'jine';
  nahradni_vozidlo?: string;
  /** false = send the customer no confirmation / change e-mail for this save. Omitted = send. */
  odeslat_email?: boolean;
  puvodni_datum_od?: string;
  puvodni_datum_do?: string;
}

export interface VacationBody {
  /** Either a real id_opravce or the string `'cely_tym'` to apply to all. */
  id_opravce: number | 'cely_tym';
  datum_od: string;
  datum_do: string;
  jmeno_zakaznika?: string;
}

export interface InternalEventBody {
  id_opravce: number;
  datum_od: string;
  datum_do: string;
  vozidlo?: string;
  spz_vozidla?: string;
  vin?: string;
  /** Free-form úkon, for every kind. */
  servisni_ukon?: string;
  druh_ukonu?: ServisDruhUkonu | 'jine';
}

export interface ShiftBody {
  id_opravce: number;
  typ_smeny: 'ranni' | 'odpoledni';
  rok: number;
  tyden: number;
}

function invalidateServis(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['servis'] });
}

// ==================== REPLACEMENT CAR ====================
// A technical reservation of a fleet car (gate: servis write + the firma's rental module — see
// `membership.nahradni_vozidla`). The reservation also lives in the rental module's caches, so those are refreshed.

/** Fleet cars free for the term; `idUdalosti` keeps the car the order already holds on offer. */
export function useServisNahradniVolnaQuery(
  term: { od: string; do: string; idUdalosti: number | null },
  enabled: boolean,
) {
  const qs = new URLSearchParams({ od: term.od, do: term.do });
  if (term.idUdalosti != null) qs.set('id_udalosti', String(term.idUdalosti));
  return useQuery({
    queryKey: ['servis', 'nahradni-vozidla', term.od, term.do, term.idUdalosti],
    enabled,
    staleTime: 0,
    queryFn: ({ signal }) =>
      api.get<{ vozidla: ServisVolneVozidlo[] }>(`/v1/servis/nahradni-vozidla?${qs}`, { signal }),
  });
}

export interface ServisNahradniBody {
  id_vozidla: number;
  /** `YYYY-MM-DDTHH:mm`, Prague local. */
  od: string;
  do: string;
  stredisko: ServisNahradniStredisko;
}

export function useSetServisNahradniMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ServisNahradniBody }) =>
      api.put<{ rezervace: ServisNahradniRezervace }>(`/v1/servis/events/${id}/nahradni-vozidlo`, body),
    onSuccess: () => {
      invalidateServis(qc);
      qc.invalidateQueries({ queryKey: ['pujcovna'] });
    },
  });
}

export function useRemoveServisNahradniMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<{ status: 'ok' }>(`/v1/servis/events/${id}/nahradni-vozidlo`),
    onSuccess: () => {
      invalidateServis(qc);
      qc.invalidateQueries({ queryKey: ['pujcovna'] });
    },
  });
}

export function useCreateServisEventMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ServisEventBody) =>
      api.post<{ status: 'ok'; id: number }>('/v1/servis/events', body),
    onSuccess: () => invalidateServis(qc),
  });
}

export function useUpdateServisEventMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ServisEventBody) =>
      api.put<{ status: 'ok' }>(`/v1/servis/events/${id}`, body),
    onSuccess: () => invalidateServis(qc),
  });
}

// The confirmation's contract is the generated one (api/openapi.json → types/api.ts): a schema change then
// surfaces here as a type error instead of a stale hand-written copy.
type Schemas = components['schemas'];

/** What confirming does (or did) to one deadline of the event's car. */
export type ServisTerminPlan = Schemas['ServisTerminPlan'];
export type ServisTerminDruh = ServisTerminPlan['druh'];

/**
 * Rental fleet cars with a km interval: the km part of the servisní prohlídka the same confirmation re-arms.
 * Null unless the car is in the fleet with an interval AND the caller may read the rental module there.
 */
export type ServisKmPlan = Schemas['ServisKmPlan'];

export type ServisConfirmResponse = Schemas['ServisEventConfirmResponse'];

/** Preview: the car of an úkon and what confirming would do to its dates. */
export type ServisTerminVozu = Schemas['ServisTerminVozu'];

export function useServisTerminyVozuQuery(
  eventId: number,
  mesice: { servis?: number; stk?: number },
  enabled = true,
) {
  const qs = new URLSearchParams();
  if (mesice.servis) qs.set('servis_mesice', String(mesice.servis));
  if (mesice.stk) qs.set('stk_mesice', String(mesice.stk));
  return useQuery({
    queryKey: ['servis', 'terminy-vozu', eventId, mesice.servis ?? null, mesice.stk ?? null],
    enabled,
    queryFn: ({ signal }) =>
      api.get<ServisTerminVozu>(`/v1/servis/events/${eventId}/terminy-vozu?${qs.toString()}`, { signal }),
    placeholderData: (previous) => previous,
  });
}

/** Confirm whether an event took place — open to every servis member (read+). */
export function useConfirmServisEventMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      stav,
      servis_mesice,
      stk_mesice,
    }: {
      id: number;
      stav: ServisPotvrzeniStav;
      /** Proběhlo only: months until the next service / STK — moves the car's dates. */
      servis_mesice?: number;
      stk_mesice?: number;
    }) =>
      api.post<ServisConfirmResponse>(`/v1/servis/events/${id}/confirm`, {
        stav,
        servis_mesice,
        stk_mesice,
      }),
    onSuccess: () => {
      invalidateServis(qc);
      // The car's STK / service dates may have moved …
      qc.invalidateQueries({ queryKey: ['ojeta'] });
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      // … and the same confirmation re-arms a rental car's km interval: the fleet list, a car's checks and the
      // dashboard feed all follow it.
      qc.invalidateQueries({ queryKey: ['pujcovna'] });
    },
  });
}

/** Clear a confirmation — write level only. */
export function useUnconfirmServisEventMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ status: 'ok' }>(`/v1/servis/events/${id}/confirm`),
    onSuccess: () => invalidateServis(qc),
  });
}

export function useDeleteServisEventMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ status: 'ok' }>(`/v1/servis/events/${id}`),
    onSuccess: () => invalidateServis(qc),
  });
}

export function useCreateVacationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VacationBody) =>
      api.post<{ status: 'ok' }>('/v1/servis/vacations', body),
    onSuccess: () => invalidateServis(qc),
  });
}

export function useUpdateVacationMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VacationBody) =>
      api.put<{ status: 'ok' }>(`/v1/servis/vacations/${id}`, body),
    onSuccess: () => invalidateServis(qc),
  });
}

export function useCreateInternalEventMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InternalEventBody) =>
      api.post<{ status: 'ok' }>('/v1/servis/internal-events', body),
    onSuccess: () => invalidateServis(qc),
  });
}

export function useUpdateInternalEventMutation(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InternalEventBody) =>
      api.put<{ status: 'ok' }>(`/v1/servis/internal-events/${id}`, body),
    onSuccess: () => invalidateServis(qc),
  });
}

export function useCreateShiftsMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ShiftBody) =>
      api.post<{ status: 'ok' }>('/v1/servis/shifts', body),
    onSuccess: () => invalidateServis(qc),
  });
}
